import type { ContributorDto, RoleSlotDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';

export function publishedModesHasOrganize(publishedModes?: { mode: string }[] | null): boolean {
  return publishedModes?.some((pm) => pm.mode === 'ORGANIZE') ?? false;
}

/** True when published to PLAY but not ORGANIZE (contributor invite still requires ORGANIZE). */
export function publishedModesIsPlayOnly(publishedModes?: { mode: string }[] | null): boolean {
  if (!publishedModes?.length) return false;
  const modes = new Set(publishedModes.map((pm) => pm.mode));
  return modes.has('PLAY') && !modes.has('ORGANIZE');
}

/** ORGANIZE documents reference their PLAN copy via `origin` (string or `{ id }`). */
function modeEntityOriginId(origin: FlowDto['origin'] | SessionDto['origin'] | undefined): string | undefined {
  if (origin == null) return undefined;
  if (typeof origin === 'string') return origin;
  if (typeof origin === 'object' && origin && 'id' in origin && typeof (origin as { id: string }).id === 'string') {
    return (origin as { id: string }).id;
  }
  return undefined;
}

/**
 * True when an ORGANIZE-mode flow exists whose PLAN id is `planFlowId`.
 * Matches backend `flowModel.exists({ origin: planFlowId, mode: ORGANIZE })`.
 */
export function flowHasOrganizeCopy(planFlowId: string | undefined, organizeFlows: FlowDto[] | undefined): boolean {
  if (!planFlowId || !organizeFlows?.length) return false;
  return organizeFlows.some((f) => f.mode === 'ORGANIZE' && modeEntityOriginId(f.origin) === planFlowId);
}

export function sessionHasOrganizeCopy(
  planSessionId: string | undefined,
  organizeSessions: SessionDto[] | undefined,
): boolean {
  if (!planSessionId || !organizeSessions?.length) return false;
  return organizeSessions.some((s) => s.mode === 'ORGANIZE' && modeEntityOriginId(s.origin) === planSessionId);
}

/**
 * Invite eligibility for a PLAN flow: prefer ORGANIZE copy in store (authoritative vs backend);
 * if organize flows are not loaded yet, fall back to PLAN `publishedModes` (legacy / partial API).
 */
export function planFlowPublishedForInvite(
  planFlow: FlowDto | undefined,
  organizeFlows: FlowDto[] | undefined,
): boolean {
  if (!planFlow?.id) return false;
  if (organizeFlows != null) {
    return flowHasOrganizeCopy(planFlow.id, organizeFlows);
  }
  return publishedModesHasOrganize(planFlow.publishedModes);
}

export function planSessionPublishedForInvite(
  planSession: SessionDto | undefined,
  organizeSessions: SessionDto[] | undefined,
): boolean {
  if (!planSession?.id) return false;
  if (organizeSessions != null) {
    return sessionHasOrganizeCopy(planSession.id, organizeSessions);
  }
  if (publishedModesIsPlayOnly(planSession.publishedModes)) {
    return false;
  }
  return publishedModesHasOrganize(planSession.publishedModes);
}

export function resolvePlanSessionIdForInvite(
  sessionRef: string | SessionDto | undefined,
  eventSessionsPlan: SessionDto[] | undefined,
  eventSessionsOrganize?: SessionDto[] | undefined,
): string | undefined {
  const planSession = resolvePlanSessionDto(sessionRef, eventSessionsPlan, eventSessionsOrganize);
  if (planSession?.id) return planSession.id;
  if (typeof sessionRef === 'object') {
    const planIdFromOrigin = modeEntityOriginId(sessionRef.origin);
    if (planIdFromOrigin) return planIdFromOrigin;
    if (sessionRef.mode === 'PLAN') return sessionRef.id;
  }
  return typeof sessionRef === 'string' ? sessionRef : undefined;
}

/**
 * Contributor invite requires an ORGANIZE session document (store match), not PLAY-only flow publication.
 * Never infer from slot-embedded `publishedModes` — PLAY-layer slot copies can be stale vs the event session list.
 */
export function isSessionOrganizePublishedForInvite(
  sessionRef: string | SessionDto | undefined,
  eventSessionsPlan: SessionDto[] | undefined,
  eventSessionsOrganize: SessionDto[] | undefined,
): boolean {
  const planId = resolvePlanSessionIdForInvite(sessionRef, eventSessionsPlan, eventSessionsOrganize);
  if (!planId) return false;

  if (eventSessionsOrganize != null) {
    return sessionHasOrganizeCopy(planId, eventSessionsOrganize);
  }

  const planSession = resolvePlanSessionDto(sessionRef, eventSessionsPlan, eventSessionsOrganize);
  if (!planSession) return false;

  if (publishedModesIsPlayOnly(planSession.publishedModes)) {
    return false;
  }
  return publishedModesHasOrganize(planSession.publishedModes);
}

export function resolvePlanFlowDto(
  entityRef: string | FlowDto | undefined,
  eventFlowsPlan: FlowDto[] | undefined,
  eventFlowsOrganize?: FlowDto[] | undefined,
): FlowDto | undefined {
  if (!eventFlowsPlan?.length && !eventFlowsOrganize?.length) return undefined;
  const id = typeof entityRef === 'string' ? entityRef : entityRef?.id;
  if (!id) return undefined;

  const planSelf = eventFlowsPlan?.find((f) => f.id === id && f.mode === 'PLAN');
  if (planSelf) return planSelf;

  const organizeInPlan = eventFlowsPlan?.find((f) => f.id === id && f.mode === 'ORGANIZE');
  if (organizeInPlan) {
    const planId = modeEntityOriginId(organizeInPlan.origin);
    if (planId) {
      const p = eventFlowsPlan?.find((f) => f.id === planId && f.mode === 'PLAN');
      if (p) return p;
    }
  }

  const organizeOnly = eventFlowsOrganize?.find((f) => f.id === id && f.mode === 'ORGANIZE');
  if (organizeOnly) {
    const planId = modeEntityOriginId(organizeOnly.origin);
    if (planId) {
      return eventFlowsPlan?.find((f) => f.id === planId && f.mode === 'PLAN');
    }
  }

  const byOriginPlan = eventFlowsPlan?.find(
    (f) => f.mode === 'PLAN' && (f.origin === id || modeEntityOriginId(f.origin) === id),
  );
  if (byOriginPlan) return byOriginPlan;

  return eventFlowsPlan?.find((f) => f.mode === 'PLAN' && f.origin === id);
}

export function resolvePlanSessionDto(
  entityRef: string | SessionDto | undefined,
  eventSessionsPlan: SessionDto[] | undefined,
  eventSessionsOrganize?: SessionDto[] | undefined,
): SessionDto | undefined {
  if (!eventSessionsPlan?.length && !eventSessionsOrganize?.length) return undefined;
  const id = typeof entityRef === 'string' ? entityRef : entityRef?.id;
  if (!id) return undefined;

  if (typeof entityRef === 'object' && (entityRef.mode as SessionDto['mode'] | 'PLAY') === 'PLAY') {
    const planIdFromPlay = modeEntityOriginId(entityRef.origin);
    if (planIdFromPlay) {
      const planFromPlay = eventSessionsPlan?.find((s) => s.id === planIdFromPlay && s.mode === 'PLAN');
      if (planFromPlay) return planFromPlay;
    }
  }

  const planSelf = eventSessionsPlan?.find((s) => s.id === id && s.mode === 'PLAN');
  if (planSelf) return planSelf;

  if (typeof entityRef === 'object') {
    const planIdFromOrigin = modeEntityOriginId(entityRef.origin);
    if (planIdFromOrigin && planIdFromOrigin !== id) {
      const planFromOrigin = eventSessionsPlan?.find((s) => s.id === planIdFromOrigin && s.mode === 'PLAN');
      if (planFromOrigin) return planFromOrigin;
    }
  }

  const organizeInPlan = eventSessionsPlan?.find((s) => s.id === id && s.mode === 'ORGANIZE');
  if (organizeInPlan) {
    const planId = modeEntityOriginId(organizeInPlan.origin);
    if (planId) {
      const p = eventSessionsPlan?.find((s) => s.id === planId && s.mode === 'PLAN');
      if (p) return p;
    }
  }

  const organizeOnly = eventSessionsOrganize?.find((s) => s.id === id && s.mode === 'ORGANIZE');
  if (organizeOnly) {
    const planId = modeEntityOriginId(organizeOnly.origin);
    if (planId) {
      return eventSessionsPlan?.find((s) => s.id === planId && s.mode === 'PLAN');
    }
  }

  const byOriginPlan = eventSessionsPlan?.find(
    (s) => s.mode === 'PLAN' && (s.origin === id || modeEntityOriginId(s.origin) === id),
  );
  if (byOriginPlan) return byOriginPlan;

  return eventSessionsPlan?.find((s) => s.mode === 'PLAN' && s.origin === id);
}

export function planEventSlotPublishedToOrganize(
  eventPlan: Pick<EventDto, 'slots'> | undefined,
  slotRef: string | undefined,
): boolean {
  if (!slotRef || !eventPlan?.slots?.length) return false;
  const s = eventPlan.slots.find((sl) => sl.id === slotRef || sl.origin === slotRef);
  return publishedModesHasOrganize(s?.publishedModes);
}

/** Narrow `role.entity` so we never pass Event/Session objects into flow plan resolution. */
function contributorFlowEntityRef(entity: ContributorDto['roles'][0]['entity']): string | FlowDto | undefined {
  if (entity == null) return undefined;
  if (typeof entity === 'string') return entity;
  if (typeof entity !== 'object') return undefined;
  if ('isInLibrary' in entity) return undefined;
  if ('durationDays' in entity && 'multiFlow' in entity) return undefined;
  if ('id' in entity && typeof (entity as { id: string }).id === 'string') {
    return entity as FlowDto;
  }
  return undefined;
}

function contributorSessionEntityRef(entity: ContributorDto['roles'][0]['entity']): string | SessionDto | undefined {
  if (entity == null) return undefined;
  if (typeof entity === 'string') return entity;
  if (typeof entity !== 'object') return undefined;
  if ('durationDays' in entity && 'multiFlow' in entity) return undefined;
  if ('areModeratorsShown' in entity || 'isFlowShown' in entity) return undefined;
  if ('isInLibrary' in entity) {
    return entity as SessionDto;
  }
  if ('id' in entity && typeof (entity as { id: string }).id === 'string') {
    return (entity as { id: string }).id;
  }
  return undefined;
}

export function isContributorFlowRoleRowInviteable(
  role: ContributorDto['roles'][0],
  eventFlowsPlan: FlowDto[] | undefined,
  eventFlowsOrganize?: FlowDto[] | undefined,
): boolean {
  if (role.entityType !== 'Flow') return true;
  const planFlow = resolvePlanFlowDto(contributorFlowEntityRef(role.entity), eventFlowsPlan, eventFlowsOrganize);
  return planFlowPublishedForInvite(planFlow, eventFlowsOrganize);
}

export function isContributorSessionRoleRowInviteable(
  role: ContributorDto['roles'][0],
  eventSessionsPlan: SessionDto[] | undefined,
  eventSessionsOrganize?: SessionDto[] | undefined,
): boolean {
  if (role.entityType !== 'Session') return true;
  const planSession = resolvePlanSessionDto(
    contributorSessionEntityRef(role.entity),
    eventSessionsPlan,
    eventSessionsOrganize,
  );
  return planSessionPublishedForInvite(planSession, eventSessionsOrganize);
}

export function isContributorFlowRoleSlotRowInviteable(
  role: ContributorDto['roles'][0],
  slot: RoleSlotDto,
  eventPlan: EventDto | undefined,
  eventFlowsPlan: FlowDto[] | undefined,
  eventFlowsOrganize?: FlowDto[] | undefined,
): boolean {
  if (!isContributorFlowRoleRowInviteable(role, eventFlowsPlan, eventFlowsOrganize)) return false;
  if (slot.entityType === 'Flow') {
    const esId = slot.eventSlotId;
    if (!esId) return true;
    return planEventSlotPublishedToOrganize(eventPlan, esId);
  }
  if (slot.entityType === 'FlowSlot') {
    const esId = slot.eventSlotId;
    if (!esId) return false;
    return planEventSlotPublishedToOrganize(eventPlan, esId);
  }
  return true;
}

export function isContributorSessionFlowSlotRowInviteable(
  role: ContributorDto['roles'][0],
  slot: RoleSlotDto,
  eventPlan: EventDto | undefined,
  eventSessionsPlan: SessionDto[] | undefined,
  eventSessionsOrganize?: SessionDto[] | undefined,
): boolean {
  if (!isContributorSessionRoleRowInviteable(role, eventSessionsPlan, eventSessionsOrganize)) return false;
  if (slot.entityType !== 'FlowSlot') return true;
  const esId = slot.eventSlotId;
  if (!esId) return false;
  return planEventSlotPublishedToOrganize(eventPlan, esId);
}

export type InvitePublicationUiContext = {
  eventPlan?: EventDto;
  eventFlowsPlan?: FlowDto[];
  eventSessionsPlan?: SessionDto[];
  /** Used with plan flows to detect ORGANIZE copies (matches backend invite validation). */
  eventFlowsOrganize?: FlowDto[];
  eventSessionsOrganize?: SessionDto[];
};

/** Filter role slots for staging / list visibility (ORGANIZE publication). */
export function filterRoleSlotsForInvitePublication(
  role: ContributorDto['roles'][0],
  slots: RoleSlotDto[] | undefined,
  ctx: InvitePublicationUiContext | undefined,
): RoleSlotDto[] {
  if (!slots?.length) return [];
  if (!ctx?.eventFlowsPlan && !ctx?.eventSessionsPlan && !ctx?.eventPlan) {
    return slots;
  }
  return slots.filter((slot) => {
    if (slot.deleted === true) return false;
    if (role.entityType === 'Flow') {
      return isContributorFlowRoleSlotRowInviteable(
        role,
        slot,
        ctx.eventPlan,
        ctx.eventFlowsPlan,
        ctx.eventFlowsOrganize,
      );
    }
    if (role.entityType === 'Session') {
      return isContributorSessionFlowSlotRowInviteable(
        role,
        slot,
        ctx.eventPlan,
        ctx.eventSessionsPlan,
        ctx.eventSessionsOrganize,
      );
    }
    return true;
  });
}

export type FlowModeratorContributionPublicationHint = 'flow' | null;
export type FlowModeratorTimeSlotPublicationHint = 'flow' | 'flowSlot' | null;
export type SessionWidgetContributionPublicationHint = 'session' | null;
export type SessionWidgetTimeSlotPublicationHint = 'session' | 'schedule' | null;

export function getFlowModeratorPublicationHints(params: {
  planFlow: FlowDto | undefined;
  eventPlan: EventDto | undefined;
  eventSlotId: string | undefined;
  eventFlowsOrganize?: FlowDto[] | undefined;
}): {
  contribution: FlowModeratorContributionPublicationHint;
  timeSlot: FlowModeratorTimeSlotPublicationHint;
} {
  const flowOk = planFlowPublishedForInvite(params.planFlow, params.eventFlowsOrganize);
  if (!flowOk) {
    return { contribution: 'flow', timeSlot: 'flow' };
  }
  if (!params.eventSlotId) {
    return { contribution: null, timeSlot: null };
  }
  const slotOk = planEventSlotPublishedToOrganize(params.eventPlan, params.eventSlotId);
  if (!slotOk) {
    return { contribution: null, timeSlot: 'flowSlot' };
  }
  return { contribution: null, timeSlot: null };
}

export function getSessionActorPublicationHints(params: {
  sessionRef: string | SessionDto | undefined;
  eventSessionsPlan?: SessionDto[] | undefined;
  eventPlan: EventDto | undefined;
  timeSlotRoleSlot: RoleSlotDto | null | undefined;
  eventSessionsOrganize?: SessionDto[] | undefined;
}): {
  contribution: SessionWidgetContributionPublicationHint;
  timeSlot: SessionWidgetTimeSlotPublicationHint;
} {
  const sessionOk = isSessionOrganizePublishedForInvite(
    params.sessionRef,
    params.eventSessionsPlan,
    params.eventSessionsOrganize,
  );
  if (!sessionOk) {
    return { contribution: 'session', timeSlot: 'session' };
  }
  if (!params.timeSlotRoleSlot || params.timeSlotRoleSlot.entityType !== 'FlowSlot') {
    return { contribution: null, timeSlot: null };
  }
  const esId = params.timeSlotRoleSlot.eventSlotId;
  const scheduleOk = planEventSlotPublishedToOrganize(params.eventPlan, esId);
  if (!scheduleOk) {
    return { contribution: null, timeSlot: 'schedule' };
  }
  return { contribution: null, timeSlot: null };
}
